
import React from 'react';
import KaTeXRenderer from './KaTeXRenderer';
import SmilesDrawerRenderer from './SmilesDrawerRenderer';

interface FormattedTextProps {
  text: string;
}

// Regex to extract SMILES code and JSON options string: CODE__{JSON_OPTIONS}__
const optionsExtractRegex = /^(.*?)__(\{.*?\})__$/;

// Combined regex for SMILES blocks and KaTeX blocks
const combinedRegex = new RegExp(
  `(SMILES\\[(.*?)\\])|(\\$(.+?)\\$)`, // Group 1: Full SMILES match, Group 2: SMILES content. Group 3: Full KaTeX match, Group 4: KaTeX content.
  'g'
);

const renderFormattedText = ({ text }: FormattedTextProps): (JSX.Element | string)[] => {
  if (typeof text !== 'string' || !text) {
    return [<React.Fragment key="empty"></React.Fragment>];
  }

  const parts: (string | { type: 'smiles'; smilesCode: string; options: Record<string, any> } | { type: 'katex'; content: string })[] = [];
  let lastIndex = 0;
  let match;

  // Reset lastIndex for global regex on each call
  combinedRegex.lastIndex = 0; 

  while ((match = combinedRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    let smilesContentToParse: string | null = null;
    // Check if the SMILES part of the regex matched (match[1] and match[2] would be populated)
    if (match[1] && match[2] !== undefined) { 
      smilesContentToParse = match[2].trim();

      let finalSmilesCode = smilesContentToParse;
      let finalOptionsObject: Record<string, any> = {};
      
      const optionMatchResult = smilesContentToParse.match(optionsExtractRegex);

      if (optionMatchResult && optionMatchResult[1] && optionMatchResult[2]) {
        finalSmilesCode = optionMatchResult[1].trim();
        try {
          finalOptionsObject = JSON.parse(optionMatchResult[2]);
        } catch (e: any) {
          // Fallback: use the code before __ as SMILES, options remain empty
        }
      } else {
        // No __JSON__ suffix, entire smilesContentToParse is the code
        finalSmilesCode = smilesContentToParse; // Ensure finalSmilesCode is set even if no options
      }
      parts.push({ type: 'smiles', smilesCode: finalSmilesCode, options: finalOptionsObject });

    } else if (match[3] && match[4] !== undefined) { // KaTeX part of the regex matched
      parts.push({ type: 'katex', content: match[4].trim() });
    }
    lastIndex = combinedRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.map((part, index) => {
    if (typeof part === 'string') {
      return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
    }
    if (part.type === 'smiles') {
      return (
        <SmilesDrawerRenderer
          key={`smiles-${index}`}
          smilesToDraw={part.smilesCode}
          drawingOptions={part.options}
        />
      );
    }
    if (part.type === 'katex') {
      return <KaTeXRenderer key={`katex-${index}`} latex={part.content} />;
    }
    return null;
  });
};

export default renderFormattedText;
